import type { ApiClientView } from '../apiClients';

/**
 * Three example API clients for `VITE_DEMO=1` (Wave 4). Every id starts with `demo-`, and their
 * key prefixes are made up — there is no key behind any of them. Read-only, like the demo users:
 * the API Keys screen offers no action on them, and every write goes to the real admin service.
 */

export const DEMO_API_CLIENT_PREFIX = 'demo-';

export function isDemoApiClientId(id: string): boolean {
  return id.startsWith(DEMO_API_CLIENT_PREFIX);
}

const DAY = 86_400_000;
const ACTOR = 'admin@kkp-poc.local (u-admin)';

export function demoApiClients(now: number): ApiClientView[] {
  const at = (days: number) => new Date(Math.floor(now / 60_000) * 60_000 + days * DAY).toISOString();
  const base = (id: string, name: string, keyPrefix: string, created: number): ApiClientView => ({
    id,
    name,
    keyPrefix,
    enabled: true,
    status: 'active',
    createdUtc: at(-created),
    createdBy: ACTOR,
    updatedUtc: at(-created),
    updatedBy: ACTOR,
    etag: `"demo-${id}"`,
  });
  return [
    {
      ...base('demo-card-switch', 'Card switch feeder (demo)', 'dEm0swch', 20),
      description: 'Pre-authorisation calls from the card switch.',
      expiresUtc: at(180),
      rotatedUtc: at(-3),
    },
    { ...base('demo-load-test', 'Load test runner (demo)', 'dEm0load', 6), enabled: false, status: 'disabled' },
    {
      ...base('demo-old-partner', 'Retired partner integration (demo)', 'dEm0part', 40),
      enabled: false,
      status: 'revoked',
      revokedUtc: at(-9),
      updatedUtc: at(-9),
    },
  ];
}

/** The slice of the API-clients client the demo layer reads through. */
export interface DemoableApiClientsApi<S> {
  list: (s: S) => Promise<ApiClientView[]>;
}

/** The demo clients in front of the real ones. Every other member — every write — is the real client, untouched. */
export function withDemoApiClients<S, A extends DemoableApiClientsApi<S>>(real: A, clock: () => number = Date.now): A {
  return {
    ...real,
    list: async (s: S) => {
      let realClients: ApiClientView[] = [];
      try {
        realClients = await real.list(s);
      } catch (e) {
        console.warn('[demo] GET api clients failed upstream; showing demo data only.', e);
      }
      return [...demoApiClients(clock()), ...realClients.filter((c) => !isDemoApiClientId(c.id))].sort((a, b) => (a.id < b.id ? -1 : 1));
    },
  };
}
